import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
    Zap,
    UploadCloud,
    Calculator,
    History,
    ChevronRight,
    BarChart2,
    Github,
    LogOut,
    User,
    ShieldAlert,
    Menu,
    X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LogoIcon from './LogoIcon';

const navItems = [
    { to: '/dashboard',  icon: BarChart2,   label: '数据概览', sublabel: 'Dashboard' },
    { to: '/classify',   icon: Zap,         label: 'AI 分类',  sublabel: 'Classify' },
    { to: '/scan',       icon: ShieldAlert, label: '风险扫描', sublabel: 'Risk Scan' },
    { to: '/batch',      icon: UploadCloud, label: '批量上传', sublabel: 'Batch Upload' },
    { to: '/tax',        icon: Calculator,  label: '税费计算', sublabel: 'Tax Calculator' },
    { to: '/history',    icon: History,     label: '历史记录', sublabel: 'History' },
];

export default function Layout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const { user, tenant, logout } = useAuth();

    const displayName = user?.name || user?.email?.split('@')[0] || '用户';
    const workspaceName = tenant?.name || '我的工作空间';
    const planLabel = tenant?.plan === 'pro' ? 'Pro' : tenant?.plan === 'enterprise' ? 'Enterprise' : 'Free';
    const planColor = tenant?.plan === 'pro'
        ? 'text-violet-400'
        : tenant?.plan === 'enterprise'
        ? 'text-amber-400'
        : 'text-slate-500';
    const quotaUsed = tenant?.classify_used ?? 0;
    const quotaTotal = tenant?.classify_quota ?? 50;
    const quotaPct = quotaTotal > 0 ? Math.min(100, Math.round(quotaUsed / quotaTotal * 100)) : 0;
    const quotaColor = quotaPct >= 90 ? 'bg-red-500' : quotaPct >= 70 ? 'bg-amber-500' : 'bg-brand-500';

    return (
        <div className="flex h-screen overflow-hidden">
            {/* Mobile backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/60 z-30 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:relative inset-y-0 left-0 z-40
                flex flex-col w-64 flex-shrink-0 bg-surface-1 border-r border-white/[0.06]
                transform transition-transform duration-200 ease-in-out
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                {/* Logo */}
                <div className="flex items-center gap-3 px-5 py-6 border-b border-white/[0.06]">
                    <LogoIcon size={36} />
                    <div className="flex-1 min-w-0">
                        <h1 className="text-sm font-bold text-white tracking-tight">税码通</h1>
                        <p className="text-[10px] text-slate-500 font-medium">海关报关 AI 平台</p>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden text-slate-500 hover:text-white transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Nav */}
                <nav className="flex-1 py-4 px-3 space-y-1">
                    <p className="section-header px-2">导航</p>
                    {navItems.map(({ to, icon: Icon, label, sublabel }) => (
                        <NavLink
                            key={to}
                            to={to}
                            className={({ isActive }) =>
                                `flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-150 group ${isActive
                                    ? 'bg-brand-600/20 text-white border border-brand-500/25'
                                    : 'text-slate-400 hover:text-white hover:bg-white/[0.04]'
                                }`
                            }
                        >
                            <div className="flex items-center gap-3">
                                <Icon size={17} className="flex-shrink-0" />
                                <div>
                                    <div className="text-sm font-medium leading-none">{label}</div>
                                    <div className="text-[10px] text-slate-600 mt-0.5">{sublabel}</div>
                                </div>
                            </div>
                            {location.pathname === to && (
                                <ChevronRight size={14} className="text-brand-400" />
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* Footer */}
                <div className="px-4 py-4 border-t border-white/[0.06] space-y-3">
                    {/* Workspace card */}
                    <div className="glass rounded-xl p-3">
                        {/* Workspace name + plan */}
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 rounded-lg bg-brand-600/30 flex items-center justify-center flex-shrink-0">
                                <LogoIcon size={16} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-white truncate">{workspaceName}</p>
                                <p className={`text-[10px] font-medium ${planColor}`}>{planLabel} 计划</p>
                            </div>
                        </div>

                        {/* Quota bar */}
                        {tenant?.plan === 'free' && (
                            <div className="mb-2">
                                <div className="flex justify-between text-[10px] text-slate-600 mb-1">
                                    <span>本月分类</span>
                                    <span className={quotaPct >= 90 ? 'text-red-400' : ''}>{quotaUsed} / {quotaTotal}</span>
                                </div>
                                <div className="h-1 bg-surface-3 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full transition-all ${quotaColor}`} style={{ width: `${quotaPct}%` }} />
                                </div>
                            </div>
                        )}

                        {/* User identity */}
                        <div className="flex items-center gap-1.5 mb-2">
                            <User size={10} className="text-slate-600 flex-shrink-0" />
                            <span className="text-[10px] text-slate-600 truncate">{displayName} · {user?.email}</span>
                        </div>

                        <button
                            onClick={logout}
                            className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-red-400 transition-colors py-1 rounded-lg hover:bg-red-500/5"
                        >
                            <LogOut size={11} />
                            退出登录
                        </button>
                    </div>

                    {/* AI info */}
                    <div className="glass rounded-xl p-3 text-xs text-slate-500 space-y-1">
                        <div className="flex justify-between">
                            <span>AI 服务商</span>
                            <span className="text-brand-400 font-medium">Qwen / DeepSeek</span>
                        </div>
                        <div className="flex justify-between">
                            <span>批量并发</span>
                            <span className="text-emerald-400 font-medium">5 并行</span>
                        </div>
                    </div>

                    <a
                        href="https://github.com"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-400 transition-colors"
                    >
                        <Github size={13} />
                        <span>GitHub 开源项目</span>
                    </a>
                </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 overflow-y-auto min-w-0">
                {/* Mobile header */}
                <div className="sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-surface-1 border-b border-white/[0.06] lg:hidden">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="text-slate-400 hover:text-white transition-colors"
                        aria-label="打开菜单"
                    >
                        <Menu size={20} />
                    </button>
                    <LogoIcon size={22} />
                    <span className="text-sm font-bold text-white">税码通</span>
                </div>
                <Outlet />
            </main>
        </div>
    );
}
